import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useRole } from '../contexts/RoleContext';
import { useGame } from '../contexts/GameContext';
import { usePlayerCache } from '../contexts/PlayerCacheContext';
import { 
  collection, 
  query, 
  where, 
  getDocs, 
  doc, 
  getDoc
} from 'firebase/firestore';
import { db } from '../firebase/config';
import { formatSkillLevel } from '../utils/formatSkillLevel';
import { getProfileForGame } from '../utils/gameProfile';
import { formatDate, sortMatchesChronologically } from '../utils/dateFormat';
import { formatScheduledDateTimeInTimezone, getDateKeyInTimezone, DEFAULT_TIMEZONE } from '../utils/dateTimeUtils';
import DataFetchOverlay from '../components/DataFetchOverlay';
import PlayerNameWithApartment from '../components/PlayerNameWithApartment';
import MatchReactionStrip from '../components/MatchReactionStrip';
import MatchReactionsModal from '../components/MatchReactionsModal';
import { useTrackEvent } from '../hooks/useTrackEvent';
import './TournamentDashboard.css';

export default function TournamentDashboard() {
  const { tournamentId } = useParams();
  const { currentUser, userProfile } = useAuth();
  const userTimezone = userProfile?.timezone || DEFAULT_TIMEZONE;
  const { canAccessAdmin, isSuperAdmin } = useRole();
  const { gameId } = useGame();
  const { getPlayers, getPlayerName, getPlayerApartmentFlat } = usePlayerCache();
  const navigate = useNavigate();
  const [tournament, setTournament] = useState(null);
  const [loading, setLoading] = useState(true);
  const [singlesLeaderboard, setSinglesLeaderboard] = useState([]);
  const [doublesLeaderboard, setDoublesLeaderboard] = useState([]);
  const [recentMatches, setRecentMatches] = useState([]);
  const [allMatches, setAllMatches] = useState([]);
  const [liveMatches, setLiveMatches] = useState([]);
  const [reactionsModalMatchId, setReactionsModalMatchId] = useState(null);
  const [tournamentStats, setTournamentStats] = useState({
    totalMatches: 0,
    completedMatches: 0,
    totalPlayers: 0,
    totalTeams: 0
  });
  const [registrations, setRegistrations] = useState([]);
  const [advancedStats, setAdvancedStats] = useState(null);
  const [copyFeedback, setCopyFeedback] = useState('');
  const [playerSchedulesExpanded, setPlayerSchedulesExpanded] = useState(false);
  const track = useTrackEvent();

  useEffect(() => {
    if (!playerSchedulesExpanded || !(registrations?.length || allMatches?.length)) return;
    const playerIds = new Set((registrations || []).map((r) => r.playerId).filter(Boolean));
    (allMatches || []).forEach((m) => {
      if (m.matchType === 'doubles') {
        (m.team1Players || []).forEach((id) => playerIds.add(id));
        (m.team2Players || []).forEach((id) => playerIds.add(id));
      } else {
        if (m.player1Id) playerIds.add(m.player1Id);
        if (m.player2Id) playerIds.add(m.player2Id);
      }
    });
    getPlayers(Array.from(playerIds));
  }, [playerSchedulesExpanded, registrations, allMatches, getPlayers]);

  useEffect(() => {
    if (tournamentId) {
      fetchTournamentData(tournamentId);
    } else {
      // If no tournamentId, find active tournament for current game
      fetchActiveTournament();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tournamentId, gameId]);

  async function fetchActiveTournament() {
    try {
      const tournamentsRef = collection(db, 'tournaments');
      const q = query(tournamentsRef, where('status', '==', 'active'));
      const snapshot = await getDocs(q);
      
      if (!snapshot.empty) {
        const all = snapshot.docs.map(d => ({ id: d.id, ...d.data(), gameId: d.data().gameId || 'table-tennis' }));
        const tournamentData = all.find(t => t.gameId === gameId) || all[0];
        if (tournamentData) {
          setTournament(tournamentData);
          fetchTournamentData(tournamentData.id);
        }
      }
    } catch (err) {
      console.error('Error fetching tournament:', err);
    } finally {
      setLoading(false);
    }
  }

  async function fetchTournamentData(id = tournamentId) {
    try {
      // Fetch tournament
      const tournamentDoc = await getDoc(doc(db, 'tournaments', id));
      if (!tournamentDoc.exists()) {
        setLoading(false);
        return;
      }
      const tournamentData = { id: tournamentDoc.id, ...tournamentDoc.data() };
      setTournament(tournamentData);

      // Fetch all matches
      const matchesSnapshot = await getDocs(
        query(collection(db, 'matches'), where('tournamentId', '==', id))
      );
      const matchesList = matchesSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setAllMatches(matchesList);

      // Fetch registrations
      const registrationsSnapshot = await getDocs(
        query(collection(db, 'registrations'), where('tournamentId', '==', id))
      );
      const regs = registrationsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setRegistrations(regs);

      // Calculate tournament stats
      const doublesRegs = regs.filter(r => r.type === 'doubles');
      const uniqueTeams = new Set(doublesRegs.filter(r => r.teamId).map(r => r.teamId));
      
      // Count unique players (not registrations, since a player can register for both singles and doubles)
      const uniquePlayerIds = new Set(regs.map(r => r.playerId));

      setTournamentStats({
        totalMatches: matchesList.length,
        completedMatches: matchesList.filter(m => m.status === 'completed').length,
        totalPlayers: uniquePlayerIds.size,
        totalTeams: uniqueTeams.size
      });

      // When no completed matches, show next 6 upcoming. Otherwise show 5 completed + 3 upcoming.
      const completed = matchesList
        .filter(m => m.status === 'completed')
        .sort(sortMatchesChronologically)
        .slice(0, 5);
      const upcoming = matchesList
        .filter(m => m.status === 'scheduled')
        .sort(sortMatchesChronologically);
      const recent = completed.length > 0
        ? [...completed, ...upcoming.slice(0, 3)]
        : upcoming.slice(0, 6);
      await fetchMatchDetails(recent);
      setRecentMatches(recent);

      // Build leaderboards
      await buildLeaderboards(matchesList, regs);

      // Calculate advanced statistics
      const stats = await calculateAdvancedStats(matchesList, regs);
      setAdvancedStats(stats);

      // Update live matches
      updateLiveMatches(matchesList);

    } catch (err) {
      console.error('Error fetching tournament data:', err);
    } finally {
      setLoading(false);
    }
  }


  async function updateLiveMatches(matches) {
    if (!matches || !Array.isArray(matches)) {
      setLiveMatches([]);
      return;
    }
    
    const live = matches.filter(m => m.status === 'in-progress');
    
    if (live.length === 0) {
      setLiveMatches([]);
      return;
    }
    
    // Fetch match details for live matches
    const matchesWithDetails = await fetchMatchDetails(live);
    
    if (!matchesWithDetails || !Array.isArray(matchesWithDetails)) {
      setLiveMatches([]);
      return;
    }
    
    // Add current scores to live matches
    const liveWithScores = matchesWithDetails.map(match => ({
      ...match,
      currentScore: match.scores ? {
        game1: match.scores.game1 || { player1: 0, player2: 0 },
        game2: match.scores.game2 || { player1: 0, player2: 0 },
        game3: match.scores.game3 || { player1: 0, player2: 0 }
      } : null
    }));
    
    setLiveMatches(liveWithScores);
  }

  async function fetchMatchDetails(matches) {
    if (!matches || matches.length === 0) {
      return [];
    }
    
    // Collect all player IDs
    const playerIds = new Set();
    matches.forEach(match => {
      if (match.matchType === 'doubles') {
        if (match.team1Players) match.team1Players.forEach(id => playerIds.add(id));
        if (match.team2Players) match.team2Players.forEach(id => playerIds.add(id));
      } else {
        if (match.player1Id) playerIds.add(match.player1Id);
        if (match.player2Id) playerIds.add(match.player2Id);
      }
      if (match.refereeId) playerIds.add(match.refereeId);
    });

    // Batch fetch all players
    await getPlayers(Array.from(playerIds));
    
    // Enrich matches with player names from cache
    matches.forEach(match => {
      if (match.matchType === 'doubles') {
        if (match.team1Players) {
          match.team1PlayerNames = match.team1Players
            .map(id => getPlayerName(id))
            .filter(name => name !== 'Unknown');
        }
        if (match.team2Players) {
          match.team2PlayerNames = match.team2Players
            .map(id => getPlayerName(id))
            .filter(name => name !== 'Unknown');
        }
      } else {
        match.player1Name = match.player1Id ? getPlayerName(match.player1Id) : 'Unknown';
        match.player2Name = match.player2Id ? getPlayerName(match.player2Id) : 'Unknown';
      }
    });
    
    return matches;
  }

  async function buildLeaderboards(matches, registrations) {
    const singlesStats = {};
    const doublesStats = {};

    // Process completed matches
    for (const match of matches.filter(m => m.status === 'completed')) {
      if (match.matchType === 'doubles') {
        // Doubles match
        const team1Id = match.team1Id;
        const team2Id = match.team2Id;
        const winnerIds = match.winnerIds || (match.winnerId ? [match.winnerId] : []);
        const isTeam1Winner = winnerIds.length > 0 && match.team1Players && 
                             match.team1Players.includes(winnerIds[0]);

        const team1Points = (match.scores?.game1?.player1 || 0) + (match.scores?.game2?.player1 || 0) + (match.scores?.game3?.player1 || 0);
        const team2Points = (match.scores?.game1?.player2 || 0) + (match.scores?.game2?.player2 || 0) + (match.scores?.game3?.player2 || 0);

        // Update team 1 stats
        if (!doublesStats[team1Id]) {
          doublesStats[team1Id] = {
            teamId: team1Id,
            teamName: match.team1Name || 'Team 1',
            players: match.team1Players || [],
            wins: 0,
            losses: 0,
            totalPoints: 0,
            pointsAgainst: 0,
            matches: []
          };
        }
        doublesStats[team1Id].totalPoints += team1Points;
        doublesStats[team1Id].pointsAgainst = (doublesStats[team1Id].pointsAgainst || 0) + team2Points;
        doublesStats[team1Id].matches.push(match);
        if (isTeam1Winner) {
          doublesStats[team1Id].wins++;
        } else {
          doublesStats[team1Id].losses++;
        }

        // Update team 2 stats
        if (!doublesStats[team2Id]) {
          doublesStats[team2Id] = {
            teamId: team2Id,
            teamName: match.team2Name || 'Team 2',
            players: match.team2Players || [],
            wins: 0,
            losses: 0,
            totalPoints: 0,
            pointsAgainst: 0,
            matches: []
          };
        }
        doublesStats[team2Id].totalPoints += team2Points;
        doublesStats[team2Id].pointsAgainst = (doublesStats[team2Id].pointsAgainst || 0) + team1Points;
        doublesStats[team2Id].matches.push(match);
        if (!isTeam1Winner) {
          doublesStats[team2Id].wins++;
        } else {
          doublesStats[team2Id].losses++;
        }
      } else {
        // Singles match
        const player1Id = match.player1Id;
        const player2Id = match.player2Id;
        const winnerId = match.winnerId;
        const player1Points = (match.scores?.game1?.player1 || 0) + (match.scores?.game2?.player1 || 0) + (match.scores?.game3?.player1 || 0);
        const player2Points = (match.scores?.game1?.player2 || 0) + (match.scores?.game2?.player2 || 0) + (match.scores?.game3?.player2 || 0);

        // Update player 1 stats
        if (!singlesStats[player1Id]) {
          singlesStats[player1Id] = {
            playerId: player1Id,
            wins: 0,
            losses: 0,
            totalPoints: 0,
            pointsAgainst: 0,
            matches: []
          };
        }
        singlesStats[player1Id].totalPoints += player1Points;
        singlesStats[player1Id].pointsAgainst = (singlesStats[player1Id].pointsAgainst || 0) + player2Points;
        singlesStats[player1Id].matches.push(match);
        if (winnerId === player1Id) {
          singlesStats[player1Id].wins++;
        } else {
          singlesStats[player1Id].losses++;
        }

        // Update player 2 stats
        if (!singlesStats[player2Id]) {
          singlesStats[player2Id] = {
            playerId: player2Id,
            wins: 0,
            losses: 0,
            totalPoints: 0,
            pointsAgainst: 0,
            matches: []
          };
        }
        singlesStats[player2Id].totalPoints += player2Points;
        singlesStats[player2Id].pointsAgainst = (singlesStats[player2Id].pointsAgainst || 0) + player1Points;
        singlesStats[player2Id].matches.push(match);
        if (winnerId === player2Id) {
          singlesStats[player2Id].wins++;
        } else {
          singlesStats[player2Id].losses++;
        }
      }
    }

    // Batch-fetch all player data via cache so display names and skills are real
    const allPlayerIds = [
      ...Object.keys(singlesStats),
      ...Object.values(doublesStats).flatMap((s) => s.players || [])
    ];
    const playersMap = allPlayerIds.length > 0 ? await getPlayers([...new Set(allPlayerIds)]) : {};
    const tournamentGameId = tournament?.gameId || gameId || 'table-tennis';
    const playerName = (id) => playersMap[id]?.displayName || playersMap[id]?.email || 'Unknown';
    const skillValueOf = (data) => {
      const sl = getProfileForGame(data || {}, tournamentGameId).skillLevel;
      return sl === 'beginner' ? 1 : sl === 'advanced' ? 3 : 2;
    };

    const singlesLeaderboardData = Object.values(singlesStats).map((stat) => {
      const data = playersMap[stat.playerId];
      const skillLevel = getProfileForGame(data || {}, tournamentGameId).skillLevel || 'average';
      const skillValue = skillValueOf(data);
      const winRate = stat.wins + stat.losses > 0
        ? stat.wins / (stat.wins + stat.losses)
        : 0;
      return {
        ...stat,
        playerName: playerName(stat.playerId),
        skillLevel,
        skillValue,
        winRate,
        totalMatches: stat.wins + stat.losses,
        losses: stat.losses
      };
    });

    const doublesLeaderboardData = Object.values(doublesStats).map((stat) => {
      const names = (stat.players || []).map((id) => playerName(id));
      let avgSkillValue = 0;
      for (const id of stat.players || []) {
        avgSkillValue += skillValueOf(playersMap[id]);
      }
      avgSkillValue = (stat.players || []).length > 0 ? avgSkillValue / (stat.players || []).length : 2;
      const winRate = stat.wins + stat.losses > 0
        ? stat.wins / (stat.wins + stat.losses)
        : 0;
      return {
        ...stat,
        playerNames: names.length ? names : ['Unknown'],
        avgSkillValue,
        winRate,
        totalMatches: stat.wins + stat.losses
      };
    });

    // Sort by wins, then by total points
    singlesLeaderboardData.sort((a, b) => {
      if (b.wins !== a.wins) return b.wins - a.wins;
      return b.totalPoints - a.totalPoints;
    });

    doublesLeaderboardData.sort((a, b) => {
      if (b.wins !== a.wins) return b.wins - a.wins;
      return b.totalPoints - a.totalPoints;
    });

    setSinglesLeaderboard(singlesLeaderboardData);
    setDoublesLeaderboard(doublesLeaderboardData);
  }

  /** Format player for summary: "Name (apt-flat)" or "Name" if no flat. */
  function formatPlayerForSummary(playerId) {
    const name = getPlayerName(playerId) || 'Unknown';
    const apt = getPlayerApartmentFlat ? getPlayerApartmentFlat(playerId) : null;
    return apt ? `${name} (${apt})` : name;
  }

  /** Build plain-text summary for sharing (e.g. WhatsApp): today's matches, players & slots, position tables. */
  function buildShareSummaryText() {
    if (!tournament) return '';
    const todayKey = getDateKeyInTimezone(Date.now(), userTimezone);
    const todayMatches = allMatches
      .filter(m => m.status === 'scheduled' && getDateKeyInTimezone(m.scheduledAt ?? m.scheduledDate, userTimezone) === todayKey)
      .sort(sortMatchesChronologically);
    const dateLabel = formatDate(new Date());

    const lines = [];
    lines.push(`${tournament.name || 'Tournament'}`);
    lines.push(`Summary – ${dateLabel}`);
    lines.push('');

    // Today's matches (chronological)
    lines.push('📅 TODAY\'S MATCHES');
    lines.push('─────────────────');
    if (todayMatches.length === 0) {
      lines.push('No matches scheduled for today.');
    } else {
      todayMatches.forEach((m, i) => {
        const timeStr = formatScheduledDateTimeInTimezone(m.scheduledAt ?? m.scheduledDate, m.scheduledTime, userTimezone, { useSeparator: ' ' });
        const p1 = m.matchType === 'doubles'
          ? (m.team1Players || []).map(id => formatPlayerForSummary(id)).filter(Boolean).join(' & ') || 'Team 1'
          : formatPlayerForSummary(m.player1Id) || 'Player 1';
        const p2 = m.matchType === 'doubles'
          ? (m.team2Players || []).map(id => formatPlayerForSummary(id)).filter(Boolean).join(' & ') || 'Team 2'
          : formatPlayerForSummary(m.player2Id) || 'Player 2';
        const refStr = m.refereeId ? formatPlayerForSummary(m.refereeId) : null;
        const typeLabel = m.matchType === 'doubles' ? ' (Doubles)' : '';
        lines.push(`${i + 1}. ${timeStr}${typeLabel}`);
        lines.push(`   ${p1} vs ${p2}`);
        if (refStr) lines.push(`   Referee: ${refStr}`);
      });
    }
    lines.push('');

    // Players required today & their time slots (chronological by first match)
    lines.push('👥 PLAYERS REQUIRED TODAY');
    lines.push('─────────────────────────');
    const playerSlots = {};
    todayMatches.forEach(m => {
      const timeStr = formatScheduledDateTimeInTimezone(m.scheduledAt ?? m.scheduledDate, m.scheduledTime, userTimezone, { useSeparator: ' ' });
      const ids = m.matchType === 'doubles'
        ? [...(m.team1Players || []), ...(m.team2Players || [])]
        : [m.player1Id, m.player2Id].filter(Boolean);
      ids.forEach(id => {
        if (!id) return;
        if (!playerSlots[id]) playerSlots[id] = [];
        if (!playerSlots[id].includes(timeStr)) playerSlots[id].push(timeStr);
      });
    });
    const sortedPlayerIds = Object.keys(playerSlots).sort((a, b) => {
      const slotsA = playerSlots[a].sort();
      const slotsB = playerSlots[b].sort();
      return (slotsA[0] || '').localeCompare(slotsB[0] || '');
    });
    if (sortedPlayerIds.length === 0) {
      lines.push('No players required today.');
    } else {
      sortedPlayerIds.forEach(id => {
        const name = formatPlayerForSummary(id);
        const slots = playerSlots[id].sort().join(', ');
        lines.push(`• ${name}: ${slots}`);
      });
    }
    lines.push('');

    // Referees required today & their time slots (chronological by first match)
    lines.push('👨‍⚖️ REFEREES REQUIRED TODAY');
    lines.push('──────────────────────────');
    const refereeSlots = {};
    todayMatches.forEach(m => {
      if (!m.refereeId) return;
      const timeStr = formatScheduledDateTimeInTimezone(m.scheduledAt ?? m.scheduledDate, m.scheduledTime, userTimezone, { useSeparator: ' ' });
      if (!refereeSlots[m.refereeId]) refereeSlots[m.refereeId] = [];
      if (!refereeSlots[m.refereeId].includes(timeStr)) refereeSlots[m.refereeId].push(timeStr);
    });
    const sortedRefereeIds = Object.keys(refereeSlots).sort((a, b) => {
      const slotsA = refereeSlots[a].sort();
      const slotsB = refereeSlots[b].sort();
      return (slotsA[0] || '').localeCompare(slotsB[0] || '');
    });
    if (sortedRefereeIds.length === 0) {
      lines.push('No referees assigned for today (assign referees on match cards).');
    } else {
      sortedRefereeIds.forEach(id => {
        const name = formatPlayerForSummary(id);
        const slots = refereeSlots[id].sort().join(', ');
        lines.push(`• ${name}: ${slots}`);
      });
    }
    lines.push('');

    // Position tables
    lines.push('📊 POSITION TABLES');
    lines.push('─────────────────');
    if (singlesLeaderboard.length > 0) {
      lines.push('Singles');
      singlesLeaderboard.slice(0, 10).forEach((p, i) => {
        const name = p.playerId ? formatPlayerForSummary(p.playerId) : (p.playerName || '—');
        lines.push(`${i + 1}. ${name}  W:${p.wins} L:${p.losses}  Pts:${p.totalPoints || 0}`);
      });
      lines.push('');
    }
    if (doublesLeaderboard.length > 0) {
      lines.push('Doubles');
      doublesLeaderboard.slice(0, 10).forEach((t, i) => {
        const names = (t.players || []).length > 0
          ? (t.players || []).map((id) => formatPlayerForSummary(id)).join(' & ')
          : (t.playerNames || []).join(' & ') || t.teamName || '—';
        lines.push(`${i + 1}. ${names}  W:${t.wins} L:${t.losses}  Pts:${t.totalPoints || 0}`);
      });
    }
    return lines.join('\n');
  }

  async function handleCopySummary() {
    track('button_click', { label: 'Copy to clipboard', category: 'tournament' });
    const text = buildShareSummaryText();
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
      setCopyFeedback('Copied! Paste in WhatsApp or any chat.');
      setTimeout(() => setCopyFeedback(''), 3000);
    } catch (err) {
      setCopyFeedback('Copy failed. Select and copy manually.');
      setTimeout(() => setCopyFeedback(''), 4000);
    }
  }

  async function calculateAdvancedStats(matches, registrations) {
    const completedMatches = matches.filter(m => m.status === 'completed');
    if (completedMatches.length === 0) return null;

    const stats = {
      // Player point statistics
      mostPointsOverall: { playerId: null, playerName: 'N/A', points: 0 },
      mostPointsInMatch: { playerId: null, playerName: 'N/A', points: 0, matchId: null },
      mostPointsInGame: { playerId: null, playerName: 'N/A', points: 0, gameNum: 0, matchId: null },
      mostPointsAgainstOpponent: { playerId: null, playerName: 'N/A', opponentId: null, opponentName: 'N/A', points: 0 },
      
      // Match statistics
      highestScoringMatch: { matchId: null, totalPoints: 0, player1Name: 'N/A', player2Name: 'N/A' },
      longestMatch: { matchId: null, totalPoints: 0, player1Name: 'N/A', player2Name: 'N/A' },
      mostDominantWin: { matchId: null, pointDiff: 0, winnerName: 'N/A', loserName: 'N/A' },
      mostCompetitiveMatch: { matchId: null, pointDiff: Infinity, player1Name: 'N/A', player2Name: 'N/A' },
      fastestWin: { matchId: null, totalPoints: Infinity, winnerName: 'N/A', loserName: 'N/A' },
      
      // Player performance statistics
      bestWinStreak: { playerId: null, playerName: 'N/A', streak: 0 },
      mostMatchesPlayed: { playerId: null, playerName: 'N/A', count: 0 },
      mostDeuceGames: { playerId: null, playerName: 'N/A', count: 0 },
      mostComebacks: { playerId: null, playerName: 'N/A', count: 0 },
      bestComeback: { playerId: null, playerName: 'N/A', deficit: 0, matchId: null },
      
      // Game statistics
      totalDeuceGames: 0,
      totalGamesPlayed: 0,
      averagePointsPerMatch: 0,
      averagePointsPerGame: 0
    };

    // Track player points
    const playerPoints = {};
    const playerPointsPerMatch = {};
    const playerPointsPerOpponent = {};
    const playerMatches = {};
    const playerWinStreaks = {};
    const playerDeuceGames = {};
    const playerComebacks = {};
    let totalMatchPoints = 0;
    let totalGamePoints = 0;
    let deuceGameCount = 0;

    for (const match of completedMatches) {
      const matchType = match.matchType || 'singles';
      const winThreshold = matchType === 'singles' ? 11 : 21;
      
      // Calculate match totals
      const matchPoints = (match.scores?.game1?.player1 || 0) + 
                         (match.scores?.game1?.player2 || 0) +
                         (match.scores?.game2?.player1 || 0) + 
                         (match.scores?.game2?.player2 || 0) +
                         (match.scores?.game3?.player1 || 0) + 
                         (match.scores?.game3?.player2 || 0);
      totalMatchPoints += matchPoints;

      // Track highest scoring match
      if (matchPoints > stats.highestScoringMatch.totalPoints) {
        stats.highestScoringMatch = {
          matchId: match.id,
          totalPoints: matchPoints,
          player1Name: match.player1Name || match.team1Name || 'Player 1',
          player2Name: match.player2Name || match.team2Name || 'Player 2'
        };
      }

      // Track longest match
      if (matchPoints > stats.longestMatch.totalPoints) {
        stats.longestMatch = {
          matchId: match.id,
          totalPoints: matchPoints,
          player1Name: match.player1Name || match.team1Name || 'Player 1',
          player2Name: match.player2Name || match.team2Name || 'Player 2'
        };
      }

      // Track fastest win
      if (matchPoints < stats.fastestWin.totalPoints) {
        const winnerName = match.winnerId === match.player1Id 
          ? (match.player1Name || 'Player 1')
          : (match.player2Name || 'Player 2');
        const loserName = match.winnerId === match.player1Id 
          ? (match.player2Name || 'Player 2')
          : (match.player1Name || 'Player 1');
        stats.fastestWin = {
          matchId: match.id,
          totalPoints: matchPoints,
          winnerName,
          loserName
        };
      }

      // Process each game
      for (let gameNum = 1; gameNum <= 3; gameNum++) {
        const gameKey = `game${gameNum}`;
        const game = match.scores?.[gameKey];
        if (!game) continue;

        const gamePoints = game.player1 + game.player2;
        totalGamePoints += gamePoints;
        stats.totalGamesPlayed++;

        // Check for deuce (10-10 for singles, 20-20 for doubles)
        const isDeuce = (game.player1 >= winThreshold - 1 && game.player2 >= winThreshold - 1) &&
                       (game.player1 === game.player2);
        if (isDeuce) {
          deuceGameCount++;
        }

        // Track most points in a single game
        if (match.matchType === 'singles') {
          const player1Id = match.player1Id;
          const player2Id = match.player2Id;
          
          // Track player points
          if (!playerPoints[player1Id]) playerPoints[player1Id] = 0;
          if (!playerPoints[player2Id]) playerPoints[player2Id] = 0;
          playerPoints[player1Id] += game.player1;
          playerPoints[player2Id] += game.player2;

          // Track points per match
          if (!playerPointsPerMatch[player1Id]) playerPointsPerMatch[player1Id] = [];
          if (!playerPointsPerMatch[player2Id]) playerPointsPerMatch[player2Id] = [];
          playerPointsPerMatch[player1Id].push({ matchId: match.id, points: game.player1 });
          playerPointsPerMatch[player2Id].push({ matchId: match.id, points: game.player2 });

          // Track points against opponent
          const key1 = `${player1Id}_${player2Id}`;
          const key2 = `${player2Id}_${player1Id}`;
          if (!playerPointsPerOpponent[key1]) playerPointsPerOpponent[key1] = { playerId: player1Id, opponentId: player2Id, points: 0 };
          if (!playerPointsPerOpponent[key2]) playerPointsPerOpponent[key2] = { playerId: player2Id, opponentId: player1Id, points: 0 };
          playerPointsPerOpponent[key1].points += game.player1;
          playerPointsPerOpponent[key2].points += game.player2;

          // Track most points in a game
          if (game.player1 > stats.mostPointsInGame.points) {
            stats.mostPointsInGame = {
              playerId: player1Id,
              playerName: match.player1Name || 'Player 1',
              points: game.player1,
              gameNum,
              matchId: match.id
            };
          }
          if (game.player2 > stats.mostPointsInGame.points) {
            stats.mostPointsInGame = {
              playerId: player2Id,
              playerName: match.player2Name || 'Player 2',
              points: game.player2,
              gameNum,
              matchId: match.id
            };
          }

          // Track deuce games per player
          if (isDeuce) {
            if (!playerDeuceGames[player1Id]) playerDeuceGames[player1Id] = 0;
            if (!playerDeuceGames[player2Id]) playerDeuceGames[player2Id] = 0;
            playerDeuceGames[player1Id]++;
            playerDeuceGames[player2Id]++;
          }

          // Track matches played
          if (!playerMatches[player1Id]) playerMatches[player1Id] = 0;
          if (!playerMatches[player2Id]) playerMatches[player2Id] = 0;
          playerMatches[player1Id]++;
          playerMatches[player2Id]++;

          // Track win streaks
          if (!playerWinStreaks[player1Id]) playerWinStreaks[player1Id] = { current: 0, best: 0 };
          if (!playerWinStreaks[player2Id]) playerWinStreaks[player2Id] = { current: 0, best: 0 };
          
          if (match.winnerId === player1Id) {
            playerWinStreaks[player1Id].current++;
            playerWinStreaks[player1Id].best = Math.max(playerWinStreaks[player1Id].best, playerWinStreaks[player1Id].current);
            playerWinStreaks[player2Id].current = 0;
          } else {
            playerWinStreaks[player2Id].current++;
            playerWinStreaks[player2Id].best = Math.max(playerWinStreaks[player2Id].best, playerWinStreaks[player2Id].current);
            playerWinStreaks[player1Id].current = 0;
          }

          // Track comebacks (winning after being behind)
          const gameWinners = match.scores?.gameWinners || {};
          let player1WasBehind = false;
          let player2WasBehind = false;
          
          if (gameNum === 2) {
            if (gameWinners.game1 === 'player2') player1WasBehind = true;
            if (gameWinners.game1 === 'player1') player2WasBehind = true;
          } else if (gameNum === 3) {
            const p1Wins = Object.values(gameWinners).filter(w => w === 'player1').length;
            const p2Wins = Object.values(gameWinners).filter(w => w === 'player2').length;
            if (p2Wins > p1Wins) player1WasBehind = true;
            if (p1Wins > p2Wins) player2WasBehind = true;
          }

          if (match.winnerId === player1Id && player1WasBehind) {
            if (!playerComebacks[player1Id]) playerComebacks[player1Id] = 0;
            playerComebacks[player1Id]++;
          }
          if (match.winnerId === player2Id && player2WasBehind) {
            if (!playerComebacks[player2Id]) playerComebacks[player2Id] = 0;
            playerComebacks[player2Id]++;
          }

          // Track best comeback (biggest deficit overcome)
          if (gameNum === 1) {
            const maxDeficit = Math.max(game.player2 - game.player1, game.player1 - game.player2);
            if (match.winnerId === player1Id && game.player2 > game.player1 && maxDeficit > stats.bestComeback.deficit) {
              stats.bestComeback = {
                playerId: player1Id,
                playerName: match.player1Name || 'Player 1',
                deficit: maxDeficit,
                matchId: match.id
              };
            }
            if (match.winnerId === player2Id && game.player1 > game.player2 && maxDeficit > stats.bestComeback.deficit) {
              stats.bestComeback = {
                playerId: player2Id,
                playerName: match.player2Name || 'Player 2',
                deficit: maxDeficit,
                matchId: match.id
              };
            }
          }

          // Track most competitive match (smallest point difference)
          const pointDiff = Math.abs(game.player1 - game.player2);
          if (pointDiff < stats.mostCompetitiveMatch.pointDiff) {
            stats.mostCompetitiveMatch = {
              matchId: match.id,
              pointDiff,
              player1Name: match.player1Name || 'Player 1',
              player2Name: match.player2Name || 'Player 2'
            };
          }

          // Track most dominant win (biggest point difference)
          const winnerPoints = match.winnerId === player1Id ? game.player1 : game.player2;
          const loserPoints = match.winnerId === player1Id ? game.player2 : game.player1;
          const diff = winnerPoints - loserPoints;
          if (diff > stats.mostDominantWin.pointDiff) {
            stats.mostDominantWin = {
              matchId: match.id,
              pointDiff: diff,
              winnerName: match.winnerId === player1Id ? (match.player1Name || 'Player 1') : (match.player2Name || 'Player 2'),
              loserName: match.winnerId === player1Id ? (match.player2Name || 'Player 2') : (match.player1Name || 'Player 1')
            };
          }
        }
      }
    }

    // Find most points overall
    for (const [playerId, points] of Object.entries(playerPoints)) {
      if (points > stats.mostPointsOverall.points) {
        const playerDoc = await getDoc(doc(db, 'users', playerId));
        const playerName = playerDoc.exists() ? (playerDoc.data().displayName || playerDoc.data().email) : 'Unknown';
        stats.mostPointsOverall = { playerId, playerName, points };
      }
    }

    // Find most points in a single match
    for (const [playerId, matches] of Object.entries(playerPointsPerMatch)) {
      for (const matchData of matches) {
        if (matchData.points > stats.mostPointsInMatch.points) {
          const playerDoc = await getDoc(doc(db, 'users', playerId));
          const playerName = playerDoc.exists() ? (playerDoc.data().displayName || playerDoc.data().email) : 'Unknown';
          stats.mostPointsInMatch = { playerId, playerName, points: matchData.points, matchId: matchData.matchId };
        }
      }
    }

    // Find most points against a specific opponent
    for (const [, data] of Object.entries(playerPointsPerOpponent)) {
      if (data.points > stats.mostPointsAgainstOpponent.points) {
        const playerDoc = await getDoc(doc(db, 'users', data.playerId));
        const opponentDoc = await getDoc(doc(db, 'users', data.opponentId));
        const playerName = playerDoc.exists() ? (playerDoc.data().displayName || playerDoc.data().email) : 'Unknown';
        const opponentName = opponentDoc.exists() ? (opponentDoc.data().displayName || opponentDoc.data().email) : 'Unknown';
        stats.mostPointsAgainstOpponent = {
          playerId: data.playerId,
          playerName,
          opponentId: data.opponentId,
          opponentName,
          points: data.points
        };
      }
    }

    // Find best win streak
    for (const [playerId, streakData] of Object.entries(playerWinStreaks)) {
      if (streakData.best > stats.bestWinStreak.streak) {
        const playerDoc = await getDoc(doc(db, 'users', playerId));
        const playerName = playerDoc.exists() ? (playerDoc.data().displayName || playerDoc.data().email) : 'Unknown';
        stats.bestWinStreak = { playerId, playerName, streak: streakData.best };
      }
    }

    // Find most matches played
    for (const [playerId, count] of Object.entries(playerMatches)) {
      if (count > stats.mostMatchesPlayed.count) {
        const playerDoc = await getDoc(doc(db, 'users', playerId));
        const playerName = playerDoc.exists() ? (playerDoc.data().displayName || playerDoc.data().email) : 'Unknown';
        stats.mostMatchesPlayed = { playerId, playerName, count };
      }
    }

    // Find most deuce games
    for (const [playerId, count] of Object.entries(playerDeuceGames)) {
      if (count > stats.mostDeuceGames.count) {
        const playerDoc = await getDoc(doc(db, 'users', playerId));
        const playerName = playerDoc.exists() ? (playerDoc.data().displayName || playerDoc.data().email) : 'Unknown';
        stats.mostDeuceGames = { playerId, playerName, count };
      }
    }

    // Find most comebacks
    for (const [playerId, count] of Object.entries(playerComebacks)) {
      if (count > stats.mostComebacks.count) {
        const playerDoc = await getDoc(doc(db, 'users', playerId));
        const playerName = playerDoc.exists() ? (playerDoc.data().displayName || playerDoc.data().email) : 'Unknown';
        stats.mostComebacks = { playerId, playerName, count };
      }
    }

    // Calculate averages
    stats.totalDeuceGames = deuceGameCount;
    stats.averagePointsPerMatch = completedMatches.length > 0 ? Math.round(totalMatchPoints / completedMatches.length) : 0;
    stats.averagePointsPerGame = stats.totalGamesPlayed > 0 ? Math.round(totalGamePoints / stats.totalGamesPlayed) : 0;

    return stats;
  }

  function calculateWinProbability(match) {
    if (!match || match.status === 'completed') return null;

    let player1Skill = 2;
    let player2Skill = 2;
    let player1Points = 0;
    let player2Points = 0;
    let player1Wins = 0;
    let player2Wins = 0;
    let player1Losses = 0;
    let player2Losses = 0;

    let player1PointsAgainst = 0;
    let player2PointsAgainst = 0;

    if (match.matchType === 'doubles') {
      // Get stats from leaderboard
      const team1Stats = doublesLeaderboard.find(t => t.teamId === match.team1Id);
      const team2Stats = doublesLeaderboard.find(t => t.teamId === match.team2Id);
      
      if (team1Stats) {
        player1Skill = team1Stats.avgSkillValue;
        player1Points = team1Stats.totalPoints;
        player1PointsAgainst = team1Stats.pointsAgainst || 0;
        player1Wins = team1Stats.wins;
        player1Losses = team1Stats.losses;
      }
      if (team2Stats) {
        player2Skill = team2Stats.avgSkillValue;
        player2Points = team2Stats.totalPoints;
        player2PointsAgainst = team2Stats.pointsAgainst || 0;
        player2Wins = team2Stats.wins;
        player2Losses = team2Stats.losses;
      }
    } else {
      // Singles
      const player1Stats = singlesLeaderboard.find(p => p.playerId === match.player1Id);
      const player2Stats = singlesLeaderboard.find(p => p.playerId === match.player2Id);
      
      if (player1Stats) {
        player1Skill = player1Stats.skillValue;
        player1Points = player1Stats.totalPoints;
        player1PointsAgainst = player1Stats.pointsAgainst || 0;
        player1Wins = player1Stats.wins;
        player1Losses = player1Stats.losses;
      }
      if (player2Stats) {
        player2Skill = player2Stats.skillValue;
        player2Points = player2Stats.totalPoints;
        player2PointsAgainst = player2Stats.pointsAgainst || 0;
        player2Wins = player2Stats.wins;
        player2Losses = player2Stats.losses;
      }
    }

    // Calculate win probability based on:
    // 1. Skill level (35% weight)
    // 2. Win rate (35% weight)
    // 3. Point differential – points for minus points against (30% weight)
    const skillDiff = player1Skill - player2Skill;
    const player1TotalMatches = player1Wins + player1Losses;
    const player2TotalMatches = player2Wins + player2Losses;
    const player1WinRate = player1TotalMatches > 0 ? player1Wins / player1TotalMatches : 0.5;
    const player2WinRate = player2TotalMatches > 0 ? player2Wins / player2TotalMatches : 0.5;
    const winRateDiff = player1WinRate - player2WinRate;

    const player1PointDiff = player1Points - player1PointsAgainst;
    const player2PointDiff = player2Points - player2PointsAgainst;
    const pointDiffDiff = player1PointDiff - player2PointDiff;
    const maxAbsPointDiff = Math.max(Math.abs(player1PointDiff), Math.abs(player2PointDiff), 1);
    const normalizedPointDiffDiff = pointDiffDiff / maxAbsPointDiff;

    const maxSkillDiff = 2; // Max difference is 2 (beginner=1 to advanced=3)
    const normalizedSkillDiff = skillDiff / maxSkillDiff;
    const normalizedWinRateDiff = winRateDiff; // Already in [-1, 1]

    const probability = 0.5 +
      (normalizedSkillDiff * 0.35) +
      (normalizedWinRateDiff * 0.35) +
      (normalizedPointDiffDiff * 0.3);

    return Math.max(0.1, Math.min(0.9, probability));
  }

  if (loading) {
    return <DataFetchOverlay message="Loading tournament dashboard…" />;
  }

  if (!tournament) {
    return (
      <div className="tournament-dashboard-container">
        <div className="tournament-error-message card-ui">
          <h2 className="tournament-error-title">No Tournament Found</h2>
          <p className="tournament-error-desc">There is no active tournament available.</p>
          <div className="tournament-error-actions">
            <button type="button" onClick={() => navigate('/dashboard')} className="btn-primary">
              Back to Dashboard
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="tournament-dashboard-container">
      <header className="tournament-dashboard-header">
        <div>
          <h1 className="page-title">{tournament.name}</h1>
          {tournament.description && (
            <p className="page-subtitle tournament-description">{tournament.description}</p>
          )}
        </div>
        <div className="tournament-header-actions">
          <button type="button" onClick={() => navigate(`/tournament/${tournament.id}/matches`)} className="btn-secondary">
            View All Matches
          </button>
          <button type="button" onClick={() => navigate('/dashboard')} className="btn-secondary">
            Back to Dashboard
          </button>
        </div>
      </header>

      {/* Tournament Stats Cards */}
      <div className="tournament-stats-grid">
        <div className="stat-card card-ui">
          <div className="stat-icon">📊</div>
          <div className="stat-content">
            <h3 className="stat-value-num">{tournamentStats.totalMatches}</h3>
            <p className="stat-label-text">Total Matches</p>
          </div>
        </div>
        <div className="stat-card card-ui">
          <div className="stat-icon">✅</div>
          <div className="stat-content">
            <h3 className="stat-value-num">{tournamentStats.completedMatches}</h3>
            <p className="stat-label-text">Completed</p>
          </div>
        </div>
        <div className="stat-card card-ui">
          <div className="stat-icon">👥</div>
          <div className="stat-content">
            <h3 className="stat-value-num">{tournamentStats.totalPlayers}</h3>
            <p className="stat-label-text">Total Players</p>
          </div>
        </div>
        <div className="stat-card card-ui">
          <div className="stat-icon">🏓</div>
          <div className="stat-content">
            <h3 className="stat-value-num">{tournamentStats.totalTeams}</h3>
            <p className="stat-label-text">Doubles Teams</p>
          </div>
        </div>
      </div>

      {/* Share summary – only for app admins or tournament system admins (creator / adminIds) */}
      {(canAccessAdmin || isSuperAdmin || (tournament && ((tournament.createdBy || '') === currentUser?.uid || (Array.isArray(tournament.adminIds) && tournament.adminIds.includes(currentUser?.uid))))) && (
        <>
        <section className="share-summary-section card-ui">
          <h2 className="tournament-section-title">Share summary (for WhatsApp / chat)</h2>
          <p className="share-summary-desc">Copy today&apos;s matches, players required & time slots, and position tables to paste in a group chat.</p>
          <div className="share-summary-actions">
            <button type="button" onClick={handleCopySummary} className="btn-primary btn-copy-summary">
              Copy to clipboard
            </button>
            {copyFeedback && <span className="share-summary-feedback">{copyFeedback}</span>}
          </div>
          <pre className="share-summary-preview" aria-label="Summary text">{buildShareSummaryText() || 'No tournament data.'}</pre>
        </section>

        {/* All player schedules – tournament admins can see every player's matches and which were modified (rescheduled) */}
        <section className="tournament-section card-ui player-schedules-section">
          <button
            type="button"
            className="tournament-section-title-btn"
            onClick={() => setPlayerSchedulesExpanded((e) => !e)}
            aria-expanded={playerSchedulesExpanded}
          >
            <h2 className="tournament-section-title">All player schedules</h2>
            <span className="expand-icon">{playerSchedulesExpanded ? '▼' : '▶'}</span>
          </button>
          {playerSchedulesExpanded && (
            <div className="player-schedules-content">
              <p className="player-schedules-desc">Schedules for all registered players. &quot;Modified&quot; means the match was rescheduled or time/venue changed.</p>
              <div className="player-schedules-table-wrap">
                <table className="player-schedules-table">
                  <thead>
                    <tr>
                      <th>Player</th>
                      <th>Match</th>
                      <th>Date / time</th>
                      <th>Status</th>
                      <th>Modified</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(() => {
                      const uniquePlayerIds = [...new Set((registrations || []).map((r) => r.playerId).filter(Boolean))];
                      const rows = [];
                      uniquePlayerIds.forEach((playerId) => {
                        const playerMatches = (allMatches || []).filter((m) => {
                          if (m.matchType === 'doubles') {
                            return (m.team1Players || []).includes(playerId) || (m.team2Players || []).includes(playerId);
                          }
                          return m.player1Id === playerId || m.player2Id === playerId;
                        }).sort(sortMatchesChronologically);
                        playerMatches.forEach((m) => {
                          const updatedMs = m.updatedAt?.toMillis ? m.updatedAt.toMillis() : (typeof m.updatedAt === 'number' ? m.updatedAt : null);
                          const createdMs = m.createdAt?.toMillis ? m.createdAt.toMillis() : (typeof m.createdAt === 'number' ? m.createdAt : null);
                          const modified = m.rescheduleRequested || m.rescheduleApproved || (updatedMs != null && createdMs != null && updatedMs > createdMs + 5000);
                          rows.push({
                            playerId,
                            match: m,
                            modified,
                          });
                        });
                      });
                      return rows.length === 0 ? (
                        <tr><td colSpan={5} className="player-schedules-empty">No scheduled matches.</td></tr>
                      ) : (
                        rows.map(({ playerId: pid, match: m, modified }) => (
                          <tr key={`${pid}-${m.id}`}>
                            <td><PlayerNameWithApartment playerId={pid} /></td>
                            <td>
                              {m.matchType === 'doubles'
                                ? `${(m.team1PlayerNames || m.team1Players || []).join(' & ')} vs ${(m.team2PlayerNames || m.team2Players || []).join(' & ')}`
                                : `${getPlayerName(m.player1Id) || m.player1Id} vs ${getPlayerName(m.player2Id) || m.player2Id}`}
                            </td>
                            <td>{formatScheduledDateTimeInTimezone(m.scheduledAt ?? m.scheduledDate, m.scheduledTime, userTimezone, { useSeparator: ' ' }) || '—'}</td>
                            <td>{m.status === 'scheduled' ? 'Scheduled' : m.status === 'in-progress' ? 'Live' : m.status === 'completed' ? 'Completed' : m.status}</td>
                            <td>{modified ? <span className="modified-badge">Modified</span> : '—'}</td>
                          </tr>
                        ))
                      );
                    })()}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </section>
        </>
      )}

      <div className="tournament-dashboard-content">
        {/* Singles Leaderboard */}
        {singlesLeaderboard.length > 0 && (
          <section className="tournament-leaderboard-section card-ui">
            <h2 className="tournament-section-title">Singles Leaderboard</h2>
            <div className="leaderboard-table">
              <div className="table-header">
                <div className="col-rank">Rank</div>
                <div className="col-name">Player</div>
                <div className="col-skill">Skill</div>
                <div className="col-wins">Wins</div>
                <div className="col-losses">Losses</div>
                <div className="col-points">Points</div>
                <div className="col-rate">Win Rate</div>
              </div>
              {singlesLeaderboard.map((player, index) => (
                <div key={player.playerId} className={`table-row ${index < 3 ? 'top-three' : ''}`}>
                  <div className="col-rank">
                    {index === 0 && <span className="medal">🥇</span>}
                    {index === 1 && <span className="medal">🥈</span>}
                    {index === 2 && <span className="medal">🥉</span>}
                    {index >= 3 && <span>{index + 1}</span>}
                  </div>
                  <div className="col-name"><PlayerNameWithApartment playerId={player.playerId} /></div>
                  <div className="col-skill">
                    <span className={`skill-badge skill-${player.skillLevel}`}>
                      {formatSkillLevel(player.skillLevel)}
                    </span>
                  </div>
                  <div className="col-wins">{player.wins}</div>
                  <div className="col-losses">{player.losses}</div>
                  <div className="col-points">{player.totalPoints}</div>
                  <div className="col-rate">{(player.winRate * 100).toFixed(1)}%</div>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Doubles Leaderboard */}
        {doublesLeaderboard.length > 0 && (
          <section className="tournament-leaderboard-section card-ui">
            <h2 className="tournament-section-title">Doubles Leaderboard</h2>
            <div className="leaderboard-table">
              <div className="table-header">
                <div className="col-rank">Rank</div>
                <div className="col-name">Players</div>
                <div className="col-skill">Avg Skill</div>
                <div className="col-wins">Wins</div>
                <div className="col-losses">Losses</div>
                <div className="col-points">Points</div>
                <div className="col-rate">Win Rate</div>
              </div>
              {doublesLeaderboard.map((team, index) => (
                <div key={team.teamId} className={`table-row ${index < 3 ? 'top-three' : ''}`}>
                  <div className="col-rank">
                    {index === 0 && <span className="medal">🥇</span>}
                    {index === 1 && <span className="medal">🥈</span>}
                    {index === 2 && <span className="medal">🥉</span>}
                    {index >= 3 && <span>{index + 1}</span>}
                  </div>
                  <div className="col-name">
                    <strong>{(team.playerNames || []).join(' & ') || team.teamName || 'Team'}</strong>
                    {(team.players || []).length > 0 && (
                      <div className="team-members">
                        {(team.players || []).map((id) => (
                          <div key={id}><PlayerNameWithApartment playerId={id} /></div>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="col-skill">
                    <span className="skill-badge">
                      {team.avgSkillValue.toFixed(1)}
                    </span>
                  </div>
                  <div className="col-wins">{team.wins}</div>
                  <div className="col-losses">{team.losses}</div>
                  <div className="col-points">{team.totalPoints}</div>
                  <div className="col-rate">{(team.winRate * 100).toFixed(1)}%</div>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Live Matches */}
        {liveMatches.length > 0 && (
          <section className="tournament-leaderboard-section card-ui">
            <h2 className="tournament-section-title">Live Matches</h2>
            <div className="live-matches-grid">
              {liveMatches.map(match => {
                const gameWinners = match.scores?.gameWinners || {};
                const p1Wins = Object.values(gameWinners).filter(w => w === 'player1').length;
                const p2Wins = Object.values(gameWinners).filter(w => w === 'player2').length;
                
                return (
                  <div key={match.id} className="live-match-card">
                    <div className="live-match-header">
                      <span className="live-badge">🔴 LIVE</span>
                      <span className={`match-type-badge ${match.matchType}`}>
                        {match.matchType === 'doubles' ? '👥 Doubles' : '🏓 Singles'}
                      </span>
                    </div>
                    <div className="live-match-players">
                      <div className="live-player">
                        <strong>
                          {match.matchType === 'doubles' 
                            ? ((match.team1Players || []).map((id) => getPlayerName(id)).filter(Boolean).join(' & ') || match.team1Name || 'Team 1')
                            : <PlayerNameWithApartment playerId={match.player1Id} />}
                        </strong>
                        {match.matchType === 'doubles' && (match.team1Players || []).length > 0 && (
                          <div className="team-players-list">
                            {(match.team1Players || []).map((id) => (
                              <small key={id}><PlayerNameWithApartment playerId={id} inline /></small>
                            ))}
                          </div>
                        )}
                        <div className="live-score">
                          {match.currentScore && (
                            <div className="game-scores">
                              <span className={gameWinners.game1 === 'player1' ? 'game-won' : ''}>
                                {match.currentScore.game1?.player1 || 0}
                              </span>
                              <span className={gameWinners.game2 === 'player1' ? 'game-won' : ''}>
                                {match.currentScore.game2?.player1 || 0}
                              </span>
                              <span className={gameWinners.game3 === 'player1' ? 'game-won' : ''}>
                                {match.currentScore.game3?.player1 || 0}
                              </span>
                            </div>
                          )}
                          <div className="match-score">{p1Wins} - {p2Wins}</div>
                        </div>
                      </div>
                      <div className="vs-live">VS</div>
                      <div className="live-player">
                        <strong>
                          {match.matchType === 'doubles' 
                            ? ((match.team2Players || []).map((id) => getPlayerName(id)).filter(Boolean).join(' & ') || match.team2Name || 'Team 2')
                            : <PlayerNameWithApartment playerId={match.player2Id} />}
                        </strong>
                        {match.matchType === 'doubles' && (match.team2Players || []).length > 0 && (
                          <div className="team-players-list">
                            {(match.team2Players || []).map((id) => (
                              <small key={id}><PlayerNameWithApartment playerId={id} inline /></small>
                            ))}
                          </div>
                        )}
                        <div className="live-score">
                          {match.currentScore && (
                            <div className="game-scores">
                              <span className={gameWinners.game1 === 'player2' ? 'game-won' : ''}>
                                {match.currentScore.game1?.player2 || 0}
                              </span>
                              <span className={gameWinners.game2 === 'player2' ? 'game-won' : ''}>
                                {match.currentScore.game2?.player2 || 0}
                              </span>
                              <span className={gameWinners.game3 === 'player2' ? 'game-won' : ''}>
                                {match.currentScore.game3?.player2 || 0}
                              </span>
                            </div>
                          )}
                          <div className="match-score">{p2Wins} - {p1Wins}</div>
                        </div>
                      </div>
                    </div>
                    {match.refereeName && (
                      <div className="live-referee">
                        Referee: {match.refereeName}
                      </div>
                    )}
                    <div className="live-match-unique-views" title="Total unique views of this match score">
                      <span className="live-match-unique-views-number">{match.viewCount ?? 0}</span>
                      <span className="live-match-unique-views-label">unique views</span>
                    </div>
                    {currentUser && (
                      <div className="live-match-reactions">
                        <div className="dashboard-reaction-team">
                          <MatchReactionStrip
                            matchId={match.id}
                            team="player1"
                            teamName={match.matchType === 'doubles' ? ((match.team1Players || []).map((id) => getPlayerName(id)).filter(Boolean).join(' & ') || match.team1Name || 'Team 1') : (getPlayerName(match.player1Id) || 'Player 1')}
                            reactionCounts={match.reactionCounts?.player1 || {}}
                            onReactionUpdate={(mid, team, emojiIndex, delta) => {
                              setLiveMatches((prev) => prev.map((m) => m.id !== mid ? m : {
                                ...m,
                                reactionCounts: {
                                  ...m.reactionCounts,
                                  [team]: {
                                    ...(m.reactionCounts?.[team] || {}),
                                    [emojiIndex]: Math.max(0, ((m.reactionCounts?.[team]?.[emojiIndex]) || 0) + delta)
                                  }
                                }
                              }));
                            }}
                            onOpenReactionsModal={setReactionsModalMatchId}
                          />
                        </div>
                        <div className="dashboard-reaction-team">
                          <MatchReactionStrip
                            matchId={match.id}
                            team="player2"
                            teamName={match.matchType === 'doubles' ? ((match.team2Players || []).map((id) => getPlayerName(id)).filter(Boolean).join(' & ') || match.team2Name || 'Team 2') : (getPlayerName(match.player2Id) || 'Player 2')}
                            reactionCounts={match.reactionCounts?.player2 || {}}
                            onReactionUpdate={(mid, team, emojiIndex, delta) => {
                              setLiveMatches((prev) => prev.map((m) => m.id !== mid ? m : {
                                ...m,
                                reactionCounts: {
                                  ...m.reactionCounts,
                                  [team]: {
                                    ...(m.reactionCounts?.[team] || {}),
                                    [emojiIndex]: Math.max(0, ((m.reactionCounts?.[team]?.[emojiIndex]) || 0) + delta)
                                  }
                                }
                              }));
                            }}
                            onOpenReactionsModal={setReactionsModalMatchId}
                          />
                        </div>
                      </div>
                    )}
                    <button 
                      onClick={() => navigate(`/match/${match.id}`)}
                      className="btn-primary btn-small"
                    >
                      View Match
                    </button>
                  </div>
                );
              })}
            </div>
          </section>
        )}

        {/* Advanced Statistics */}
        {advancedStats && (
          <section className="tournament-leaderboard-section card-ui">
            <h2 className="tournament-section-title">Tournament Statistics</h2>
            <div className="stats-grid-detailed">
              <div className="stat-category">
                <h3>🏆 Player Records</h3>
                <div className="stat-item">
                  <span className="stat-label">Most Points Overall:</span>
                  <span className="stat-value">{advancedStats.mostPointsOverall.playerName} ({advancedStats.mostPointsOverall.points} pts)</span>
                </div>
                <div className="stat-item">
                  <span className="stat-label">Most Points in a Match:</span>
                  <span className="stat-value">{advancedStats.mostPointsInMatch.playerName} ({advancedStats.mostPointsInMatch.points} pts)</span>
                </div>
                <div className="stat-item">
                  <span className="stat-label">Most Points in a Game:</span>
                  <span className="stat-value">{advancedStats.mostPointsInGame.playerName} ({advancedStats.mostPointsInGame.points} pts, Game {advancedStats.mostPointsInGame.gameNum})</span>
                </div>
                <div className="stat-item">
                  <span className="stat-label">Most Points vs Opponent:</span>
                  <span className="stat-value">{advancedStats.mostPointsAgainstOpponent.playerName} vs {advancedStats.mostPointsAgainstOpponent.opponentName} ({advancedStats.mostPointsAgainstOpponent.points} pts)</span>
                </div>
                <div className="stat-item">
                  <span className="stat-label">Best Win Streak:</span>
                  <span className="stat-value">{advancedStats.bestWinStreak.playerName} ({advancedStats.bestWinStreak.streak} wins)</span>
                </div>
                <div className="stat-item">
                  <span className="stat-label">Most Matches Played:</span>
                  <span className="stat-value">{advancedStats.mostMatchesPlayed.playerName} ({advancedStats.mostMatchesPlayed.count} matches)</span>
                </div>
                <div className="stat-item">
                  <span className="stat-label">Most Deuce Games:</span>
                  <span className="stat-value">{advancedStats.mostDeuceGames.playerName} ({advancedStats.mostDeuceGames.count} games)</span>
                </div>
                <div className="stat-item">
                  <span className="stat-label">Most Comebacks:</span>
                  <span className="stat-value">{advancedStats.mostComebacks.playerName} ({advancedStats.mostComebacks.count} comebacks)</span>
                </div>
                <div className="stat-item">
                  <span className="stat-label">Best Comeback:</span>
                  <span className="stat-value">{advancedStats.bestComeback.playerName} (overcame {advancedStats.bestComeback.deficit} point deficit)</span>
                </div>
              </div>

              <div className="stat-category">
                <h3>🎯 Match Records</h3>
                <div className="stat-item">
                  <span className="stat-label">Highest Scoring Match:</span>
                  <span className="stat-value">{advancedStats.highestScoringMatch.player1Name} vs {advancedStats.highestScoringMatch.player2Name} ({advancedStats.highestScoringMatch.totalPoints} pts)</span>
                </div>
                <div className="stat-item">
                  <span className="stat-label">Longest Match:</span>
                  <span className="stat-value">{advancedStats.longestMatch.player1Name} vs {advancedStats.longestMatch.player2Name} ({advancedStats.longestMatch.totalPoints} pts)</span>
                </div>
                <div className="stat-item">
                  <span className="stat-label">Fastest Win:</span>
                  <span className="stat-value">{advancedStats.fastestWin.winnerName} vs {advancedStats.fastestWin.loserName} ({advancedStats.fastestWin.totalPoints} pts)</span>
                </div>
                <div className="stat-item">
                  <span className="stat-label">Most Dominant Win:</span>
                  <span className="stat-value">{advancedStats.mostDominantWin.winnerName} vs {advancedStats.mostDominantWin.loserName} ({advancedStats.mostDominantWin.pointDiff} pt difference)</span>
                </div>
                <div className="stat-item">
                  <span className="stat-label">Most Competitive Match:</span>
                  <span className="stat-value">{advancedStats.mostCompetitiveMatch.player1Name} vs {advancedStats.mostCompetitiveMatch.player2Name} ({advancedStats.mostCompetitiveMatch.pointDiff} pt difference)</span>
                </div>
              </div>

              <div className="stat-category">
                <h3>📈 Tournament Averages</h3>
                <div className="stat-item">
                  <span className="stat-label">Average Points per Match:</span>
                  <span className="stat-value">{advancedStats.averagePointsPerMatch} pts</span>
                </div>
                <div className="stat-item">
                  <span className="stat-label">Average Points per Game:</span>
                  <span className="stat-value">{advancedStats.averagePointsPerGame} pts</span>
                </div>
                <div className="stat-item">
                  <span className="stat-label">Total Deuce Games:</span>
                  <span className="stat-value">{advancedStats.totalDeuceGames}</span>
                </div>
                <div className="stat-item">
                  <span className="stat-label">Total Games Played:</span>
                  <span className="stat-value">{advancedStats.totalGamesPlayed}</span>
                </div>
              </div>
            </div>
          </section>
        )}

        {/* Recent Matches */}
        {recentMatches.length > 0 && (
          <section className="tournament-recent-section card-ui">
            <h2 className="tournament-section-title">{recentMatches.some(m => m.status === 'completed') ? 'Recent Matches' : 'Upcoming Matches'}</h2>
            <div className="matches-list">
              {recentMatches.map(match => {
                const winProb = calculateWinProbability(match);
                const isCompleted = match.status === 'completed';
                return (
                  <div key={match.id} className="match-card">
                    <div className="match-header-small">
                      <span className={`match-type-badge ${match.matchType}`}>
                        {match.matchType === 'doubles' ? '👥 Doubles' : '🏓 Singles'}
                      </span>
                      {(match.scheduledDate || match.completedAt) && (
                        <span className="match-date">
                          {match.scheduledDate
                            ? formatScheduledDateTimeInTimezone(match.scheduledAt ?? match.scheduledDate, match.scheduledTime, userTimezone, { useSeparator: ', ' })
                            : formatDate(match.completedAt)}
                        </span>
                      )}
                    </div>
                    <div className="match-players-small">
                      <div className="player-result">
                        <strong>
                          {match.matchType === 'doubles' 
                            ? (match.team1Name || 'Team 1')
                            : <PlayerNameWithApartment playerId={match.player1Id} />}
                        </strong>
                        {match.matchType === 'doubles' && (match.team1Players || []).length > 0 && (
                          <div className="team-players-list">
                            {(match.team1Players || []).map((id) => (
                              <small key={id}><PlayerNameWithApartment playerId={id} inline /></small>
                            ))}
                          </div>
                        )}
                        {isCompleted && match.scores && (
                          <span className="score">
                            {Object.values(match.scores.gameWinners || {}).filter(w => w === 'player1').length}
                          </span>
                        )}
                        {isCompleted && match.winnerIds && match.team1Players && 
                         match.team1Players.includes(match.winnerIds[0]) && (
                          <span className="winner-badge">✓ Winner</span>
                        )}
                        {isCompleted && match.winnerId === match.player1Id && (
                          <span className="winner-badge">✓ Winner</span>
                        )}
                      </div>
                      <div className="vs-small">VS</div>
                      <div className="player-result">
                        <strong>
                          {match.matchType === 'doubles' 
                            ? (match.team2Name || 'Team 2')
                            : <PlayerNameWithApartment playerId={match.player2Id} />}
                        </strong>
                        {match.matchType === 'doubles' && (match.team2Players || []).length > 0 && (
                          <div className="team-players-list">
                            {(match.team2Players || []).map((id) => (
                              <small key={id}><PlayerNameWithApartment playerId={id} inline /></small>
                            ))}
                          </div>
                        )}
                        {isCompleted && match.scores && (
                          <span className="score">
                            {Object.values(match.scores.gameWinners || {}).filter(w => w === 'player2').length}
                          </span>
                        )}
                        {isCompleted && match.winnerIds && match.team2Players && 
                         match.team2Players.includes(match.winnerIds[0]) && (
                          <span className="winner-badge">✓ Winner</span>
                        )}
                        {isCompleted && match.winnerId === match.player2Id && (
                          <span className="winner-badge">✓ Winner</span>
                        )}
                      </div>
                    </div>
                    {match.refereeId && (
                      <div className="match-referee-small">
                        Referee: <PlayerNameWithApartment playerId={match.refereeId} inline />
                      </div>
                    )}
                    {winProb !== null && match.status === 'scheduled' && (
                      <div className="win-probability">
                        <div className="prob-bar">
                          <div 
                            className="prob-fill" 
                            style={{ width: `${winProb * 100}%` }}
                          >
                            {match.matchType === 'doubles' 
                              ? (match.team1Name || 'Team 1')
                              : <><PlayerNameWithApartment playerId={match.player1Id} inline />: {(winProb * 100).toFixed(0)}%</>}
                          </div>
                          <div className="prob-fill-2" style={{ width: `${(1 - winProb) * 100}%` }}>
                            {match.matchType === 'doubles' 
                              ? (match.team2Name || 'Team 2')
                              : <><PlayerNameWithApartment playerId={match.player2Id} inline />: {((1 - winProb) * 100).toFixed(0)}%</>}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </section>
        )}
      </div>
      {reactionsModalMatchId && (() => {
        const m = liveMatches.find((match) => match.id === reactionsModalMatchId);
        return m ? (
          <MatchReactionsModal
            matchId={reactionsModalMatchId}
            player1Name={m.matchType === 'doubles' ? ((m.team1Players || []).map((id) => getPlayerName(id)).filter(Boolean).join(' & ') || m.team1Name || 'Team 1') : (getPlayerName(m.player1Id) || 'Player 1')}
            player2Name={m.matchType === 'doubles' ? ((m.team2Players || []).map((id) => getPlayerName(id)).filter(Boolean).join(' & ') || m.team2Name || 'Team 2') : (getPlayerName(m.player2Id) || 'Player 2')}
            onClose={() => setReactionsModalMatchId(null)}
          />
        ) : null;
      })()}
    </div>
  );
}

